import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Trash2, Copy, Image as ImageIcon } from "lucide-react";

export default function MediaPage() {
  const [media, setMedia] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    api.get("/api/v1/media").then(setMedia).catch((e) => toast.error(e.message)).finally(() => setLoading(false));
  }, []);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const result = await api.upload("/api/v1/media", fd);
      setMedia([{ id: result.id, filename: file.name, url: result.url, mime_type: file.type, size: file.size }, ...media]);
      toast.success("Uploaded!");
    } catch (e: any) { toast.error(e.message); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Delete this file?")) return;
    try { await api.delete(`/api/v1/media/${id}`); setMedia(media.filter(m => m.id !== id)); toast.success("Deleted"); }
    catch (e: any) { toast.error(e.message); }
  };

  const copyUrl = (url: string) => { navigator.clipboard.writeText(url); toast.success("URL copied!"); };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Media</h1>
        <label className={`px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 flex items-center gap-2 cursor-pointer ${uploading ? "opacity-50 pointer-events-none" : ""}`}>
          <Upload className="w-4 h-4" /> {uploading ? "Uploading..." : "Upload"}
          <input ref={fileRef} type="file" className="hidden" onChange={handleUpload} accept="image/*,video/*,audio/*,.pdf,.zip" />
        </label>
      </div>
      {loading ? <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">{[1,2,3,4].map(i=><div key={i} className="aspect-square bg-muted rounded-xl animate-pulse"/>)}</div> : media.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">No media files yet.</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {media.map(m => (
            <div key={m.id} className="bg-card rounded-xl border border-border overflow-hidden group">
              <div className="aspect-square bg-muted flex items-center justify-center">
                {m.mime_type?.startsWith("image/") ? <img src={m.url} alt={m.filename} className="w-full h-full object-cover" /> : <ImageIcon className="w-8 h-8 text-muted-foreground" />}
              </div>
              <div className="p-3">
                <p className="text-xs font-medium truncate">{m.filename}</p>
                <div className="flex items-center gap-1 mt-2">
                  <button onClick={() => copyUrl(m.url)} className="p-1 rounded hover:bg-accent" title="Copy URL"><Copy className="w-3.5 h-3.5 text-muted-foreground" /></button>
                  <button onClick={() => handleDelete(m.id)} className="p-1 rounded hover:bg-destructive/10" title="Delete"><Trash2 className="w-3.5 h-3.5 text-muted-foreground hover:text-destructive" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
